import { EdgeBucketing } from './edge-bucketing';
import type { EdgeBucketingDef, FeatureFlagDef } from './flag-types';

/**
 * Lift an `EdgeBucketing` entry into the `bucketing` half of a flag def.
 *
 * Field by field rather than by spread: `EdgeBucketingDef` also carries `key`,
 * which belongs on the def itself, and spreading would leave a second copy of
 * it nested inside `bucketing` where no type mentions it.
 */
const bucketingOf = <T extends EdgeBucketingDef>(def: T) =>
  ({
    mode: 'edge',
    enrollment: def.enrollment,
    split: def.split,
    fallbackVariant: def.fallbackVariant,
  }) as const;

/**
 * Edge-bucketed flags ONLY, in their own module.
 *
 * `ClientFlags` (in `./client-flags`) spreads this in, so app-side consumers
 * see edge + non-edge flags as one registry — but pulling `./client-flags`
 * into the CloudFront bundle would ship every `local`/`user` flag definition
 * with it, because rolldown can't tree-shake object properties and
 * `@listening/frontend` isn't marked `sideEffects: false` (see
 * docs/projects/complete/a-b-test-public-doc.md §6 + the assertion in
 * `packages/infra/src/web/edge.ts`). Hence this module. Mirrors the
 * `public-doc-category-slugs.ts` edge-isolation pattern.
 *
 * The same argument applies one level down, which is why the defs below are
 * COMPOSED rather than written out: the edge reads only `key`, `enrollment`,
 * `split` and `fallbackVariant`, so those live in `./edge-bucketing` and the
 * viewer-request handler imports that module instead of this one. `variants`,
 * `default` and `bucketing.mode` are added here, where nothing pays for them.
 * An experiment's `pinning` and `exposureAt` are split out one step further,
 * into `./edge-experiment-policies`. All three modules are keyed by the same
 * flag names and the type system requires every key in all of them.
 *
 * Add an experiment in `./edge-bucketing`, its variants below, its policy in
 * `./edge-experiment-policies`, and a route registration in
 * `experimentalRoutes`.
 *
 * The `@__PURE__` IIFE is load-bearing, not style. `experimental-routes.ts`
 * imports this module for `edgeExperimentCacheShape` and is itself reachable
 * from the edge bundle; rolldown drops the object but cannot prove the
 * `EdgeBucketing.x.key` reads and `bucketingOf` calls below are side-effect-
 * free, so without the annotation it leaves them behind as bare expression
 * statements — measured at 101 bytes of pure residue. Same trick, same reason,
 * as `sharedCachedPathPatterns`.
 */
export const EdgeClientFlags = /* @__PURE__ */ (() =>
  ({
    /**
     * Does the account-first squeeze + onboarding funnel beat the existing
     * academic landing page on paid traffic?
     *
     * `control` renders today's page (`Homepage25Body` → signup → app);
     * `treatment` renders the funnel squeeze page, whose CTAs lead into
     * `/onboarding/*`. Both arms live on the SAME URLs
     * (`/academic` + `/fb/academic`) precisely so PostHog can compute
     * significance over a randomized assignment — comparing two different
     * landing URLs is a quasi-experiment its stats engine cannot score.
     *
     * `enrollment: 1` (not the usual 0.1 ramp): the whole point is a clean
     * 50/50 read on bought traffic, and a 10% enrollment would compare 5%
     * against 5% while 90% of the spend produced untracked control views.
     *
     * See `docs/reference/experiments/academic-landing-funnel-v1.md`.
     */
    academicLandingFunnelV1: {
      key: EdgeBucketing.academicLandingFunnelV1.key,
      variants: ['control', 'treatment'] as const,
      default: 'control' as const,
      bucketing: bucketingOf(EdgeBucketing.academicLandingFunnelV1),
    },
    /**
     * Does routing the homepage's signup CTAs into the onboarding funnel beat
     * the existing `/app/continue` destination?
     *
     * Unlike `academicLandingFunnelV1`, the page itself is identical in both
     * arms — only where its CTAs point changes. `control` keeps today's
     * `/app/continue`; `treatment` sends the clicks to `/onboarding/welcome`.
     * EVERY signup CTA on the page moves together (nav, hero, SaveTime, demo
     * banner, app sliders, testimonials, FAQ, closing banner) — they are one
     * conversion path, and moving a subset would leave visitors who used the
     * others in the treatment denominator without the treatment. The flag is
     * read in the `/` route component, NOT inside `Homepage25Body` (which also
     * renders as the academic experiment's control arm) — see
     * `apps/start/src/routes/_marketing/index.tsx`.
     *
     * `enrollment: 1` with a 50/50 split, same shape as the academic test:
     * the CTA destination is invisible until clicked, so there is no visual
     * risk to ramp gradually for, and full enrollment gives the fastest read.
     *
     * See `docs/reference/experiments/homepage-cta-funnel-v1.md`.
     */
    homepageCtaFunnelV1: {
      key: EdgeBucketing.homepageCtaFunnelV1.key,
      variants: ['control', 'treatment'] as const,
      default: 'control' as const,
      bucketing: bucketingOf(EdgeBucketing.homepageCtaFunnelV1),
    },
    /**
     * Two-arm annual price test — the Mode C consumer. The variants are edge
     * codes (`a` = `control`, `b` = `price-59-trial`), decoded with
     * `decodePricingEdgeCode` from `@listening/api-contract/pricing-experiment`;
     * `test/bucketing/pricing-split-parity.test.ts` pins them to the contract.
     *
     * `default: 'a'` is what a not-yet-applied read returns; the crawler /
     * cookie-less fallback is the top-priced arm `b`, declared in
     * `./edge-bucketing`. See `BucketingConfig.fallbackVariant`.
     */
    pricingTestV2: {
      key: EdgeBucketing.pricingTestV2.key,
      variants: ['a', 'b'] as const,
      default: 'a' as const,
      bucketing: bucketingOf(EdgeBucketing.pricingTestV2),
    },
  }) as const satisfies Record<string, FeatureFlagDef>)();

// C4 is refused at load time for this definition — see the assert in
// `./client-flags`, which composes the app-facing registry. It cannot live
// here: this module is value-reachable from the CloudFront bundle (via
// `experimental-routes`), and a module-scope assert is a side effect
// rolldown cannot tree-shake, so it would drag the whole pricing contract
// past the edge byte budget.
